import { Check } from "lucide-react";
import { PageHero, CTABand } from "@/components/site/PageHero";
import { Container, Overline, Reveal } from "@/lib/ui";
import { IMAGES } from "@/data/site";

const CONTENT = {
  "property-management": {
    overline: "Property Management",
    title: "Day-to-day operations that protect income and elevate experience.",
    lead: "We run properties to an institutional standard — disciplined leasing, revenue optimization, responsive maintenance and a resident experience that drives retention.",
    image: IMAGES.hero,
    intro: "Property management is where investment theses are won or lost. Our on-site and centralized teams execute a rigorous operating playbook that protects net operating income while building the resident loyalty that underpins durable occupancy.",
    capabilities: [
      ["Leasing & Marketing", "Demand generation, tour-to-lease conversion and brand-consistent marketing across every channel."],
      ["Revenue Management", "Dynamic pricing and lease-term strategy calibrated to submarket conditions."],
      ["Maintenance Operations", "Preventive programs, rapid response and vendor SLAs that preserve asset condition."],
      ["Resident Experience", "Service standards and communication that improve renewals and reputation."],
      ["Vendor Management", "Competitive sourcing, compliance and performance oversight of the vendor base."],
      ["Occupancy Optimization", "Renewal strategy, churn analysis and lease expiration management."],
    ],
  },
  "asset-management": {
    overline: "Asset Management",
    title: "Strategic oversight aligned to your return objectives.",
    lead: "We translate ownership strategy into operating plans — then hold the portfolio accountable with institutional reporting, budgeting and performance analytics.",
    image: IMAGES.skyline,
    intro: "Asset management is the bridge between daily operations and investor returns. We set the business plan for each asset, monitor performance against underwriting, and recommend the capital and operating decisions that compound long-term value.",
    capabilities: [
      ["Multifamily Asset Management", "Business-plan execution and performance management across residential portfolios."],
      ["Financial Reporting", "Audit-ready statements tailored to each mandate and reporting cadence."],
      ["Budgeting & Forecasting", "Bottom-up budgets and reforecasts tied to market and operating data."],
      ["Owner Reporting", "Transparent, KPI-driven reporting from asset to portfolio level."],
      ["Capital Improvement Management", "Prioritized capex planning and delivery oversight that maximizes ROI."],
      ["Performance Optimization", "NOI growth strategies grounded in analytics and benchmarking."],
    ],
  },
  "hospitality-management": {
    overline: "Hospitality Management",
    title: "Hospitality and short-term operations at institutional standards.",
    lead: "We operate hospitality and short-term rental assets with the same discipline we bring to multifamily — revenue management, guest experience and rigorous compliance.",
    image: IMAGES.lobby,
    intro: "Hospitality demands operational precision and revenue sophistication. Our teams manage distribution, pricing, housekeeping and the guest journey end-to-end, maximizing RevPAR while protecting brand and asset condition.",
    capabilities: [
      ["Short-Term Rental Management", "Full-service operations for STR and furnished portfolios."],
      ["Dynamic Revenue Management", "Data-driven pricing across seasons, events and demand cycles."],
      ["Guest Experience", "Five-star service standards that drive ratings and repeat demand."],
      ["Housekeeping & Turns", "Consistent, inspected turnovers at scale."],
      ["Channel & Distribution", "Optimized presence across booking platforms and direct channels."],
      ["Compliance", "Licensing, tax and regulatory compliance across jurisdictions."],
    ],
  },
  "portfolio-transition": {
    overline: "Investment & Transaction Support",
    title: "Underwriting-grade diligence and seamless transitions.",
    lead: "From acquisition diligence to disposition readiness, we support owners across the transaction lifecycle with zero disruption to income.",
    image: IMAGES.commercial,
    intro: "Transitions are the moments of greatest operational risk. Our onboarding and diligence teams execute controlled takeovers, integrate systems and staff, and prepare assets for a premium exit — protecting performance every step of the way.",
    capabilities: [
      ["Due Diligence", "Operational and financial diligence that validates the underwriting."],
      ["Acquisition Transition", "Controlled takeovers with continuity of income and service."],
      ["Disposition Support", "Data rooms, reporting and operational readiness for exit."],
      ["Portfolio Onboarding", "Rapid, standardized onboarding across scattered or consolidated portfolios."],
      ["Operational Integration", "Systems, staffing and vendor alignment from day one."],
      ["Owner Representation", "Acting in the owner's interest across every operating decision."],
    ],
  },
};

export default function ServiceDetail({ slug }) {
  const c = CONTENT[slug];
  if (!c) return null;
  return (
    <>
      <PageHero id={slug} overline={c.overline} title={c.title} lead={c.lead} image={c.image} />

      <section className="py-24 md:py-32 bg-[#F7F7F5]">
        <Container>
          <div className="max-w-3xl">
            <Overline>Overview</Overline>
            <p className="mt-6 font-display font-light text-2xl md:text-3xl leading-snug text-[#121A16]">{c.intro}</p>
          </div>

          <div className="mt-16">
            <Overline>Capabilities</Overline>
            <div className="mt-8 grid md:grid-cols-2 gap-px bg-[#E5E5DF] border border-[#E5E5DF]">
              {c.capabilities.map(([t, d], i) => (
                <Reveal key={t} delay={(i % 2) * 0.06}>
                  <div className="bg-[#F7F7F5] p-8 h-full">
                    <div className="flex items-center gap-2.5 mb-2">
                      <Check className="w-4 h-4 text-[#275C43]" />
                      <h3 className="font-display text-xl text-[#121A16]">{t}</h3>
                    </div>
                    <p className="text-sm text-[#5C6660] leading-relaxed">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <CTABand />
    </>
  );
}
